import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { subscribeToStepState, subscribeToTourLifecycle, advanceTour } from './guideHelper';
import { isPersistedId } from './idHelper';

// Re-export the imperative tour driver so screens have one import location
// for tour-control utilities. Most screens should prefer useAdvanceOnStep
// below, which guards against advancing from the wrong step.
export { advanceTour };

// useTourDataRefresh — fires a refresh callback whenever the tour-active
// state toggles for a hub-style screen. Detail screens drive their tour
// data through useTour + SET_RECORD dispatches, but hub screens (Cultivation
// Hub, Schedule, etc.) load via `useEffect(() => loadData(), [])` and never
// re-fetch unless something nudges them. The tour interceptor only sees
// FUTURE axios requests — without this hook, the hub starts the tour with
// whatever real data it loaded on mount, and the mocks register no effect.
//
// Usage:
//   const loadData = useCallback(..., [deps]);
//   useEffect(() => { loadData(); }, [loadData]);   // initial load (unchanged)
//   useTourDataRefresh(loadData, common.screens.cultivationHub);
//
// Fires loadData() on:
//   - tour start  (false → true)  → interceptor serves mock data
//   - tour end    (true  → false) → interceptor falls through, real data
// Does NOT fire on initial mount (the component's own loader handles that).
export function useTourDataRefresh(refreshFn, screenId) {
    const isMatch = useSelector(state =>
        screenId
            ? state.help?.activeTourScreen === screenId
            : !!state.help?.activeTourScreen
    );
    const refreshRef = useRef(refreshFn);
    useEffect(() => { refreshRef.current = refreshFn; }, [refreshFn]);

    const prevActiveRef = useRef(false);
    useEffect(() => {
        if (prevActiveRef.current === isMatch) return;
        prevActiveRef.current = isMatch;
        // Fire on transitions only; skip the false→false no-op on mount.
        refreshRef.current?.();
    }, [isMatch]);
}

// useAdvanceOnStep — returns a click handler that advances the tour ONLY
// when it's currently on the given step. Designed for hub/list screens that
// want "click here → next step" UX, especially when the next step's target
// is inside a dialog/panel opened by the click itself.
//
// Usage:
//   const advanceFromRoomsStep = useAdvanceOnStep('cultivation-hub-rooms');
//   const onRoomClick = (room) => {
//       openRoomDialog(room);
//       advanceFromRoomsStep();  // no-op unless tour is on that step
//   };
//
// The matching is by `step.id` from the migration (e.g. 'cultivation-hub-
// rooms', 'sched-task-row'). If the user clicks the element outside a tour
// or on a different step, the call is a silent no-op — the screen handler
// stays clean of `if (isTourActive())` gating.
//
// Timing: openRoomDialog() schedules a React render; advanceFromRoomsStep()
// dispatches Joyride's next() synchronously. Joyride uses a MutationObserver
// to wait for the next step's target, so the dialog mounting after the
// dispatch is fine — Joyride finds the new DOM once it appears.
export function useAdvanceOnStep(stepId) {
    const isCurrent = useSelector(state => state.help?.activeTourStep === stepId);
    return useCallback(() => {
        if (isCurrent) advanceTour();
    }, [isCurrent]);
}

// useRecordIdForFetch — hand a record-scoped widget the id it should actually
// fetch with.
//
// A detail tour simulates a record by swapping a readable mock id
// ('productionBom-tour-mock') into the screen's formData. That id flows down
// into every child that takes the record id as a prop — including the ones
// that are never part of the simulation: Comments, Attachments and Print all
// show real, user-owned data that no tour step ever mocks. They would fetch
// against the mock id, which the API can only answer with a CastError 500.
//
// So those three ask for the id here instead of using the prop directly, and
// get the real record back while the tour runs. The pair is published by
// guideHelper on every step (not by useTour), so it covers the screens that
// still hand-roll their own step subscription too. The substitution is deliberately
// narrow — it happens ONLY when the id handed in is exactly the mock id that
// replaced this screen's record. A mock id belonging to something else (a
// simulated CAPA row in a dialog, say) is left alone rather than silently
// re-pointed at the screen's record, which would show the wrong record's
// comments. Those keep the isPersistedId guards at their own call sites.
//
// Outside a tour this returns the id untouched, so it costs a selector read.
export function useRecordIdForFetch(id) {
    const mockId = useSelector(state => state.help?.tourMockRecordId || null);
    const realId = useSelector(state => state.help?.tourRealRecordId || null);
    if (mockId == null || id == null || id !== mockId) return id;
    return isPersistedId(realId) ? realId : id;
}

// useTour — standardized JoyRide tour wiring for detail screens.
//
// Detail screens that drive a state-simulation tour (sopui/src/training/<screen>.js)
// historically reimplemented the same ~30-line subscription block per file —
// and the copy-paste drifted across the codebase, producing stale-_id races,
// inconsistent restore-on-finish behavior, and missing per-step UI hooks. This
// hook consolidates the pattern so each screen declares its tour with a
// single call and inherits every fix made here for free.
//
// === Wiring contract ===
//
// A consuming screen must:
//   1. Use a reducer (or reducer-shaped state) with two action types — one for
//      "merge record payload into formData" (typically SET_RECORD) and one for
//      "set a single field" (typically SET_FIELD). The hook dispatches both:
//      SET_RECORD on each step, SET_FIELD { field: '_id', value: routeId }
//      on tour:end so the screen's _id reverts to the URL param.
//   2. Treat routeId (from react-router useParams) as the source of truth
//      for "which real record this page is showing". The hook hands routeId
//      back via SET_FIELD on tour:end, so the screen's loadRecord should
//      prefer URL id over closure / ref _id.
//   3. Gate its data-fetch function with isTourActive() — once mock data has
//      landed (tourActiveRef latched true), any refetch would clobber the
//      simulation. The hook also clears the latch BEFORE invoking onTourEnd,
//      so onTourEnd can safely call the very same loadRecord without
//      re-triggering the guard.
//
// === Why the latch & not a useState ===
// Subscribers fire from outside React's scheduler; a setState would batch and
// risk us answering an isTourActive() call with stale state. A ref read inside
// the loadRecord guard is sync and always current.
export function useTour({
    screenId,           // common.screens.<name>
    dispatch,           // reducer dispatch
    setRecordType,      // action type for "merge full record"
    setFieldType,       // action type for "set single field"
    routeId,            // URL param from useParams — source of truth
    resolveStatus,      // optional (record) => record — sentinel resolver
    onTourEnd,          // optional () => void — typically refetches real data
    onStepChange,       // optional (stepNumber, stepData) => void
    currentRecord,      // optional — the screen's live record (formData). Snapshotted
                        // at tour start so tour-end can restore it when the tour was
                        // launched on a NEW/blank screen with no real id to refetch.
} = {}) {
    const tourActiveRef = useRef(false);
    // Snapshot of the screen's record as it was the instant the tour started.
    // Used to undo the simulation when there is no real record to refetch.
    const preTourRecordRef = useRef(null);

    // Mirror callback / value inputs into refs so the subscription effect
    // can subscribe ONCE on mount but its handlers always see fresh values.
    // Without this, the screen would either re-subscribe on every render
    // (effect deps churn) or capture a stale closure (no deps).
    const routeIdRef = useRef(routeId);
    const resolveStatusRef = useRef(resolveStatus);
    const onTourEndRef = useRef(onTourEnd);
    const onStepChangeRef = useRef(onStepChange);
    const dispatchRef = useRef(dispatch);
    const currentRecordRef = useRef(currentRecord);
    useEffect(() => { routeIdRef.current = routeId; }, [routeId]);
    useEffect(() => { resolveStatusRef.current = resolveStatus; }, [resolveStatus]);
    useEffect(() => { onTourEndRef.current = onTourEnd; }, [onTourEnd]);
    useEffect(() => { onStepChangeRef.current = onStepChange; }, [onStepChange]);
    useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);
    useEffect(() => { currentRecordRef.current = currentRecord; }, [currentRecord]);

    useEffect(() => {
        if (!screenId || !setRecordType || !setFieldType) return undefined;

        const unsubStep = subscribeToStepState((stepData) => {
            if (stepData?._screenId !== screenId || !stepData.record) return;
            // First mock emit for this screen — latch the tour as active so
            // loadRecord guards via isTourActive() fire from this point on.
            // Snapshot the pre-tour record on this same first emit, BEFORE the
            // mock SET_RECORD dispatch below renders — currentRecordRef still
            // holds the real/blank record the user was looking at.
            if (!tourActiveRef.current) {
                tourActiveRef.current = true;
                preTourRecordRef.current = currentRecordRef.current || null;
            }

            const record = resolveStatusRef.current
                ? resolveStatusRef.current(stepData.record)
                : stepData.record;
            dispatchRef.current({ type: setRecordType, payload: record });

            // Per-step UI hooks (e.g. expanding a locally-stateful section).
            // Anything that can't ride along on the SET_RECORD payload goes here.
            onStepChangeRef.current?.(stepData._stepNumber, stepData);
        });

        const unsubLifecycle = subscribeToTourLifecycle((evt) => {
            if (evt?.type !== 'end' || evt.screenId !== screenId) return;
            if (!tourActiveRef.current) return;

            // Clear the latch BEFORE onTourEnd runs — that way the screen's
            // loadRecord, which guards on isTourActive(), proceeds to refetch.
            tourActiveRef.current = false;

            // Restore the real record id on the reducer so any downstream
            // effect that reads formData._id (e.g. derived crumbs, child
            // dialogs) sees the real value. The actual record body is
            // overwritten by the onTourEnd refetch a moment later.
            const realId = routeIdRef.current;
            if (realId != null && realId !== '') {
                dispatchRef.current({ type: setFieldType, field: '_id', value: realId });
            } else if (preTourRecordRef.current != null) {
                // The tour was launched on a NEW/blank record — there is no
                // real id to refetch, so onTourEnd's loadRecord will bail on
                // the mock sentinel and leave the simulated record on screen
                // (with its mock-id child links that navigate to dead routes).
                // Restore the exact pre-tour record to wipe the simulation.
                // SET_RECORD merges over state, and the snapshot is the full
                // record object, so every mocked field is overwritten back.
                dispatchRef.current({ type: setRecordType, payload: preTourRecordRef.current });
            }
            preTourRecordRef.current = null;

            onTourEndRef.current?.();
        });

        return () => {
            unsubStep();
            unsubLifecycle();
        };
    }, [screenId, setRecordType, setFieldType]);

    // Stable accessor — callers (loadRecord, side-effects) can early-return
    // on isTourActive() without managing their own ref.
    const isTourActive = useCallback(() => tourActiveRef.current, []);

    return { isTourActive };
}

// Resolve a '__<PREFIX>_<key>__' status sentinel against a tenant status map.
// The training mocks can't ship real ObjectIds (those vary per tenant), so
// they encode the lifecycle key as a sentinel string; the subscriber swaps
// it for the real id at runtime. This helper is shared across screens so
// every training file's sentinel format matches what's resolved here.
//
// Example: '__BATCH_STATUS_signedOff__' → resolves against batchStatuses.signedOff
//          '__SALE_STATUS_invoiced__'   → resolves against saleStatuses.invoiced
//
// If statusMap is missing or the key isn't in it, returns the record
// unchanged — the screen's downstream comparisons just won't match (harmless
// for the tour, vs. crashing on a missing id).
export function resolveStatusSentinel(record, statusMap, prefix = '__BATCH_STATUS_') {
    if (typeof record?.status !== 'string' || !record.status.startsWith(prefix)) {
        return record;
    }
    // Canonical sentinel format is `<prefix><key>__` (double trailing
    // underscore). All training files must match — keeps this slice strict
    // so a typo in a future training file fails fast instead of silently
    // mapping to a wrong status key.
    const key = record.status.slice(prefix.length, -2);
    const realId = statusMap?.[key];
    if (realId == null) return record;
    return { ...record, status: realId };
}
